<script setup lang="ts">
/**
 * The tail-wagging cat shown while a page change is still resolving (its chunk downloading, the
 * access guard answering). Mounted once in App.vue, so it covers every page of the app.
 *
 * Appears only after a navigation has been pending for a moment (`useRouteLoading`), so quick hops
 * never flash it. `pointer-events: none` throughout: it is feedback, never a blocker — the user can
 * still tap the tab bar or another link, which simply supersedes the pending navigation.
 *
 * The drawing is `public/cat-loader.svg`, the same file the boot splash in index.html uses.
 *
 * ⚠️ `useScope: "global"` — a bare `useI18n()` in a child binds to an empty local scope and renders
 * raw `alkokh.*` keys. See docs/AGENT.md.
 */
import { useRouteLoading } from "@/composables/useRouteLoading";

const { t } = useI18n({ useScope: "global" });
const { isRouteLoading } = useRouteLoading();

// `BASE_URL` is "/" on the web and "./" in Electron (file://), matching index.html's %BASE_URL%.
const catSrc = `${import.meta.env.BASE_URL}cat-loader.svg`;
</script>

<template>
  <Transition name="route-loading-fade">
    <div
      v-if="isRouteLoading"
      class="route-loading-overlay"
      role="status"
      aria-live="polite"
    >
      <img
        class="route-loading-overlay__cat"
        :src="catSrc"
        width="480"
        height="480"
        alt=""
      >
      <span class="route-loading-overlay__label">{{ t("alkokh.global.loader.page") }}</span>
    </div>
  </Transition>
</template>

<style lang="scss">
.route-loading-overlay {
  position: fixed;
  z-index: 1005; // above page content and the app bar, just under the phone tab bar (1006)
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  // Near-opaque: the cat takes most of the screen, so the page behind should not compete with it.
  background: rgba(var(--v-theme-background), 0.92);
  gap: 0.5rem;
  inset: 0;
  pointer-events: none;

  // Most of the screen — the larger square that still fits with the label, in either orientation.
  &__cat {
    block-size: min(80vw, 65vh);
    inline-size: min(80vw, 65vh);
  }

  &__label {
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    font-size: 1rem;
    font-weight: 500;
  }
}

.route-loading-fade-enter-active,
.route-loading-fade-leave-active {
  transition: opacity 0.15s ease;
}

.route-loading-fade-enter-from,
.route-loading-fade-leave-to {
  opacity: 0;
}
</style>
